import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';

/**
 * PUXAR PARA ATUALIZAR, SÓ NO APLICATIVO (22/10/2026).
 *
 * Vale nas telas de LISTA: a dos simulados do aluno, as notificações e a lista do professor (com o acompanhar, que mora
 * nela) — `telaQuePuxa`. NUNCA na prova: puxar ali não pode recarregar nada (a rota da prova não está na lista, por
 * construção). Com a tela no topo, puxar para baixo mais de `LIMIAR` px e soltar recarrega os DADOS da tela (as consultas
 * ativas do React Query), não a página inteira. Os ouvintes são passivos: a rolagem continua do jeito de sempre.
 */
const LIMIAR = 70;

export function telaQuePuxa(caminho: string): boolean {
  const c = caminho.replace(/\/$/, '') || '/';
  return c === '/student' || c === '/notificacoes' || c === '/teacher/simulations';
}

export function PuxarParaAtualizar({ ligado }: { ligado: boolean }) {
  const queryClient = useQueryClient();
  const [puxado, setPuxado] = useState(0);
  const [atualizando, setAtualizando] = useState(false);
  const inicio = useRef<number | null>(null);
  const puxadoRef = useRef(0);

  useEffect(() => {
    if (!ligado) return;
    const noTopo = () => {
      const main = document.querySelector('.main-layout-wrapper main') as HTMLElement | null;
      return window.scrollY <= 0 && (!main || main.scrollTop <= 0);
    };
    const aoTocar = (e: TouchEvent) => { inicio.current = e.touches.length === 1 && noTopo() ? e.touches[0].clientY : null; };
    const aoMover = (e: TouchEvent) => {
      if (inicio.current == null) return;
      const d = e.touches[0].clientY - inicio.current;
      puxadoRef.current = d > 0 && noTopo() ? Math.min(d * 0.5, 110) : 0;
      setPuxado(puxadoRef.current);
    };
    const aoSoltar = () => {
      const passou = puxadoRef.current >= LIMIAR * 0.5;
      inicio.current = null; puxadoRef.current = 0; setPuxado(0);
      if (!passou) return;
      setAtualizando(true);
      void queryClient.invalidateQueries({ refetchType: 'active' }).finally(() => setAtualizando(false));
    };
    window.addEventListener('touchstart', aoTocar, { passive: true });
    window.addEventListener('touchmove', aoMover, { passive: true });
    window.addEventListener('touchend', aoSoltar, { passive: true });
    window.addEventListener('touchcancel', aoSoltar, { passive: true });
    return () => {
      window.removeEventListener('touchstart', aoTocar);
      window.removeEventListener('touchmove', aoMover);
      window.removeEventListener('touchend', aoSoltar);
      window.removeEventListener('touchcancel', aoSoltar);
    };
  }, [ligado, queryClient]);

  if (!ligado || (!puxado && !atualizando)) return null;
  const pronto = puxado >= LIMIAR * 0.5;
  return (
    <div
      className="pointer-events-none fixed inset-x-0 z-[55] flex justify-center"
      style={{ top: `calc(env(safe-area-inset-top) + 52px + ${atualizando ? 8 : Math.max(0, puxado - 24)}px)` }}
      data-testid="puxar-para-atualizar"
      aria-live="polite"
    >
      <span className="rounded-full bg-white px-3 py-1.5 text-xs font-medium text-gray-700 shadow ring-1 ring-gray-200">
        {atualizando ? 'Atualizando…' : pronto ? 'Solte para atualizar' : 'Puxe para atualizar'}
      </span>
    </div>
  );
}
